import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, defineScreen } from '@/shared/screen/define';
import type { ScreenManifest } from '@/shared/screen/types';

import { automaticSplitFilters } from './automatic-split.filters';
import { AUTOMATIC_SPLIT_PERMISSION } from './automatic-split.sources';
import { NEW_AUTOMATIC_SPLIT_ID } from './automatic-split.wire';

// Manifestos do Split automático (U23/U42). Arquivo leve: entra no `src/app/screens.ts` e no ⌘K. As ações
// presas à operação aberta ligam o `run` real na tela (`screens/automatic-split-agent.ts`).

export const AUTOMATIC_SPLIT_PATH = '/contas-digitais/split-automatico';
export const automaticSplitPlanPath = (id: string) => `${AUTOMATIC_SPLIT_PATH}/${encodeURIComponent(id)}`;
export const automaticSplitFormPath = (id: string) => `${AUTOMATIC_SPLIT_PATH}/${encodeURIComponent(id)}/detalhes`;

/**
 * A rota inteira só existe com `FEATURE_FLAGS.AUTOMATIC_SPLIT` verdadeira (`router.tsx:645`). O manifesto só
 * sabe comparar igualdade: aqui recusa a flag ausente; o "verdadeiro" completo fica na tela (ver SPEC).
 */
const ACCESS = { flag: { key: 'FEATURE_FLAGS.AUTOMATIC_SPLIT', notEquals: null } } as const;

const needsScreen = () => {
  throw new Error('This action needs the automatic split screen open with the operation loaded.');
};

// ─── lista ─────────────────────────────────────────────────────────────────────────────────────────

export const automaticSplitCreate = action({
  id: 'automaticSplit.create',
  kind: 'navigate',
  label: msg`Cadastrar nova operação`,
  description: msg`Abre o formulário em branco de uma nova operação de split automático (conta escrow, favorecidos, configurações financeiras e plano de parcelas).`,
  keywords: [msg`split`, msg`escrow`, msg`nova operação`],
  shortcut: 'n',
  run: ({ nav }) => nav.to(automaticSplitFormPath(NEW_AUTOMATIC_SPLIT_ID)),
});

export const automaticSplitOpen = action({
  id: 'automaticSplit.open',
  kind: 'navigate',
  row: true,
  page: true,
  label: msg`Abrir plano de pagamento`,
  description: msg`Abre o plano de pagamento (parcelas e transferências) de uma operação de split automático pelo id.`,
  input: z.object({ id: z.string().min(1).max(64) }),
  run: ({ nav, input }) => nav.to(automaticSplitPlanPath(input.id)),
});

export const automaticSplitFilter = filterAction('automaticSplit.filter', automaticSplitFilters, {
  description: msg`Filtra as operações de split automático: searchString (qualquer campo), f_creditorName (credor), f_borrowerName (tomador) e f_account (conta escrow). Filtro omitido é limpo.`,
});

export const automaticSplitListPage = listPageAction('automaticSplit.list.page');

export const automaticSplitListScreen = defineScreen({
  id: 'automaticSplit.list',
  path: AUTOMATIC_SPLIT_PATH,
  title: msg`Operações de Split automático`,
  description: msg`Operações de split automático do correspondente (recebimentos da conta escrow repartidos entre credores e tomador): lista paginada com filtros, abrir o plano de pagamento e cadastrar nova operação.`,
  permission: AUTOMATIC_SPLIT_PERMISSION,
  access: ACCESS,
  place: { icon: 'landmark', section: 'bank-account' },
  actions: [automaticSplitCreate, automaticSplitFilter, automaticSplitListPage, automaticSplitOpen],
});

// ─── plano de pagamento ────────────────────────────────────────────────────────────────────────────

export const automaticSplitPlanRefresh = action({
  id: 'automaticSplit.plan.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo o plano de pagamento da operação aberta na tela. Nada é alterado.`,
  run: needsScreen,
});

export const automaticSplitOpenAccount = action({
  id: 'automaticSplit.plan.openAccount',
  kind: 'navigate',
  label: msg`Acessar conta`,
  description: msg`Abre a tela Em construção no lugar da conta escrow da operação aberta na tela, na mesma aba (o link da conta não sai do novo UYZY).`,
  run: needsScreen,
});

export const automaticSplitEdit = action({
  id: 'automaticSplit.plan.edit',
  kind: 'navigate',
  label: msg`Editar operação`,
  description: msg`Abre o formulário de edição da operação aberta na tela.`,
  run: needsScreen,
});

export const automaticSplitTransfers = action({
  id: 'automaticSplit.plan.transfers',
  kind: 'draft',
  row: true,
  label: msg`Ver transferências`,
  description: msg`Abre o painel de transferências de uma parcela da operação aberta na tela, pelo número da parcela (só parcelas que têm transferências). Quem salva uma mudança de status é a pessoa.`,
  input: z.strictObject({ installment: z.number().int().min(1).max(999) }),
  run: ({ nav, input }) => nav.search({ parcela: input.installment }),
});

export const automaticSplitPlanPage = listPageAction('automaticSplit.plan.page');

export const installmentStatusInput = z.strictObject({ status: z.number().int().min(1).max(5) });

export const automaticSplitInstallmentStatus = action({
  id: 'automaticSplit.installmentStatus',
  kind: 'write',
  label: msg`Salvar status da parcela`,
  description: msg`Muda o status da parcela cujo painel de transferências está aberto (1 em aberto, 2 parcialmente paga, 3 paga, 4 em atraso, 5 cancelada), como o Salvar desse painel. Abra o painel antes.`,
  input: installmentStatusInput,
  preview: () => ({ title: i18n._(msg`Salvar status da parcela`) }),
  run: needsScreen,
});

export const automaticSplitPlanScreen = defineScreen({
  id: 'automaticSplit.plan',
  path: `${AUTOMATIC_SPLIT_PATH}/$operationId`,
  title: msg`Plano de pagamento`,
  description: msg`Plano de pagamento de uma operação de split automático: parcelas com valor, valor pago, juros, multa, vencimento, data de pagamento e status; as transferências de cada parcela e a mudança manual do status da parcela.`,
  permission: AUTOMATIC_SPLIT_PERMISSION,
  access: ACCESS,
  place: { icon: 'receipt-text', parent: 'automaticSplit.list', record: msg`Plano de pagamento` },
  actions: [automaticSplitOpenAccount, automaticSplitPlanRefresh, automaticSplitEdit, automaticSplitTransfers, automaticSplitPlanPage, automaticSplitInstallmentStatus],
});

// ─── formulário ────────────────────────────────────────────────────────────────────────────────────

export const automaticSplitOpenEscrow = action({
  id: 'automaticSplit.form.escrow',
  kind: 'draft',
  label: msg`Dados da conta escrow`,
  description: msg`Abre o painel da conta escrow do formulário (número da conta; banco UY3 SCD e agência 1 são fixos). Ao salvar, carrega os favorecidos da conta.`,
  run: ({ nav }) => nav.search({ sheet: 'escrow' }),
});

export const automaticSplitOpenFinancial = action({
  id: 'automaticSplit.form.financial',
  kind: 'draft',
  label: msg`Configurações financeiras`,
  description: msg`Abre o painel de configurações financeiras do formulário (valor, juros ao mês, multa, parcelas, primeiro vencimento, percentual sobre a parcela, dia da transferência do troco, pagamento parcial). Ao salvar, simula o plano.`,
  run: ({ nav }) => nav.search({ sheet: 'configuracoes' }),
});

export const automaticSplitCancel = action({
  id: 'automaticSplit.cancel',
  kind: 'destructive',
  stepUp: true,
  label: msg`Cancelar parcelas`,
  description: msg`Cancela as parcelas da operação aberta no formulário de edição. Pede senha e código MFA digitados pela pessoa: a ferramenta só abre o painel de confirmação.`,
  input: z.strictObject({}),
  preview: () => ({ title: i18n._(msg`Cancelar parcelas`), tone: 'destructive' }),
  run: needsScreen,
});

export const automaticSplitFormScreen = defineScreen({
  id: 'automaticSplit.form',
  path: `${AUTOMATIC_SPLIT_PATH}/$operationId/detalhes`,
  title: msg`Operação de split automático`,
  description: msg`Cria ou edita uma operação de split automático: conta escrow, favorecidos (titular e credores com proporções), configurações financeiras com simulação do plano e o plano de parcelas editável; na edição, cancela parcelas com senha e MFA.`,
  permission: AUTOMATIC_SPLIT_PERMISSION,
  access: ACCESS,
  place: { icon: 'file-text', parent: 'automaticSplit.list', record: msg`Operação de split automático` },
  actions: [automaticSplitOpenEscrow, automaticSplitOpenFinancial, automaticSplitCancel],
});

export const AUTOMATIC_SPLIT_SCREENS: readonly ScreenManifest[] = [automaticSplitListScreen, automaticSplitPlanScreen, automaticSplitFormScreen];
